import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useSnackbar } from "../contexts/SnackbarContext";
import PublicMarketingShell from "../components/PublicMarketingShell";
import {
  publicCardSx,
  publicHeroChipSx,
  publicSubtitleSx,
  publicTitleSx,
  PM_COLORS,
} from "../constants/publicMarketingTheme";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    color: PM_COLORS.textSecondary,
    "& fieldset": { borderColor: "rgba(56, 189, 248, 0.28)" },
    "&:hover fieldset": { borderColor: "rgba(56, 189, 248, 0.45)" },
  },
  "& .MuiInputLabel-root": { color: PM_COLORS.textMuted },
};

const SALES_EMAIL = "sales@jdintegrations.net";
const SALES_PHONE_DISPLAY = "0936 948 1844";
/** E.164 for tel: links (PH mobile 09… → +63…) */
const SALES_PHONE_TEL = "+639369481844";

const ContactPage = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(
        "/api/public/contact",
        { name, email, company, message },
        { withCredentials: true },
      );
      showSnackbar({
        message: "Message sent. We will get back to you soon.",
        severity: "success",
        duration: 6000,
      });
      setName("");
      setEmail("");
      setCompany("");
      setMessage("");
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Could not send your message. Try email or phone below.";
      showSnackbar({ message: msg, severity: "error", duration: 8000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <PublicMarketingShell>
      <Container maxWidth="lg">
        <Paper
          component="form"
          onSubmit={handleSubmit}
          sx={{
            ...publicCardSx,
            p: 3,
            animation: "fadeUp 650ms ease-out",
          }}
        >
          <Chip label="Get in touch" sx={publicHeroChipSx} />
          <Typography variant="h3" sx={{ ...publicTitleSx, mb: 1 }}>
            Contact Us
          </Typography>
          <Typography sx={{ ...publicSubtitleSx, mb: 2 }}>
            Talk to our team about onboarding, implementation, and module strategy.
          </Typography>
          <Stack spacing={1.25}>
            <Typography>
              Email:{" "}
              <Link href={`mailto:${SALES_EMAIL}`} color="secondary" fontWeight={600}>
                {SALES_EMAIL}
              </Link>
            </Typography>
            <Typography>
              Phone:{" "}
              <Link href={`tel:${SALES_PHONE_TEL}`} color="secondary" fontWeight={600}>
                {SALES_PHONE_DISPLAY}
              </Link>{" "}
              (Philippines)
            </Typography>
            <Typography>Office hours: Monday–Friday, 9:00 AM–6:00 PM (PH)</Typography>
          </Stack>

          <Typography variant="h6" sx={{ mt: 3, mb: 1.5, fontWeight: 700 }}>
            Send a message
          </Typography>
          <Stack spacing={2} sx={{ maxWidth: 560 }}>
            <TextField
              label="Your name"
              color="secondary"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              autoComplete="name"
              sx={fieldSx}
            />
            <TextField
              label="Email"
              color="secondary"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              fullWidth
              autoComplete="email"
              sx={fieldSx}
            />
            <TextField
              label="Company (optional)"
              color="secondary"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              fullWidth
              autoComplete="organization"
              sx={fieldSx}
            />
            <TextField
              label="Message"
              color="secondary"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              fullWidth
              multiline
              minRows={4}
              placeholder="How can we help?"
              sx={fieldSx}
            />
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={sending}
              sx={{ alignSelf: "flex-start", fontWeight: 700, borderRadius: 2 }}
            >
              {sending ? "Sending…" : "Send message"}
            </Button>
          </Stack>

          <Button
            variant="contained"
            color="secondary"
            sx={{ mt: 2, fontWeight: 700, borderRadius: 2 }}
            onClick={() => navigate("/signup")}
          >
            Start Your Setup
          </Button>
        </Paper>
      </Container>
    </PublicMarketingShell>
  );
};

export default ContactPage;
